import React from 'react';
import {
  Grid, Typography, Card,
  Box,
} from '@mui/material';
import { Redirect } from 'react-router-dom';
import jwtDecode from '../../utils/jwtDecode';
import { getLoanFetcher } from '../../queries/account';
import type { LoanOverViewType, UserAccountComponent, LoanData } from '../../types/account';
import DataLoading from '../../components/DataLoading';
import LoanOverview from '../../feature/account/LoanOverview';
import ViewTitle from '../../components/ViewTitle';
import analytics from '../../utils/analytics';
import SurveyCard from '../../feature/account/ProductsList/surveyCard';
import AdvanceRequestCard from '../../feature/account/AdvanceRequestCard';
import AdvanceOfferDialog from '../../feature/account/AdvanceOfferDialog';
import { sx } from './styles';
import ShoppingRedirectCard from '../../feature/account/ShoppingRedirectCard';
import NoActiveLoansBanner from '../../feature/account/NoActiveLoansBanner';
import { useEligibility } from '../../context/EligibilityContext';

type AccountStatus = 'loading' | 'hasLoans' | 'investor' | 'onlyBnpl' | 'noLoans';

const BNPL_REDIRECT_KEY = 'pu-bnpl-redirect-done';

function RedirectOnce({ to, sessionKey }: { to: string, sessionKey: string }) {
  sessionStorage.setItem(sessionKey, 'true');
  return <Redirect to={to} />;
}

export default function AccountIndex(props: UserAccountComponent) {
  const { token } = props;
  const { firstName, hasInvestment } = jwtDecode(token);
  const { eligible, plrStep, loading: eligibilityLoading } = useEligibility();

  const [loanData, setLoanData] = React.useState<LoanData['data']|null>(null);
  const [loanDataLoading, setLoanDataLoading] = React.useState(true);
  const [loanDataError, setLoanDataError] = React.useState(false);

  React.useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    getLoanFetcher(token)
      .then((res) => { if (!cancelled) setLoanData(res.data); })
      .catch(() => { if (!cancelled) setLoanDataError(true); })
      .finally(() => { if (!cancelled) setLoanDataLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  const { status, nonBnplLoans } = React.useMemo(() => {
    if (loanDataLoading || eligibilityLoading) {
      return { status: 'loading' as AccountStatus, nonBnplLoans: [] };
    }
    const loans = loanDataError || !loanData ? [] : loanData;
    const nonBnpl = loans.filter((loan: LoanOverViewType) => loan.productType !== 'bnpl');

    if (nonBnpl.length > 0) return { status: 'hasLoans' as AccountStatus, nonBnplLoans: nonBnpl };
    if (hasInvestment) return { status: 'investor' as AccountStatus, nonBnplLoans: nonBnpl };
    if (loans.length > 0) return { status: 'onlyBnpl' as AccountStatus, nonBnplLoans: nonBnpl };
    return { status: 'noLoans' as AccountStatus, nonBnplLoans: nonBnpl };
  }, [loanDataLoading, eligibilityLoading, loanDataError, loanData, hasInvestment]);

  const bnplRedirectPending = React.useRef(
    sessionStorage.getItem(BNPL_REDIRECT_KEY) !== 'true',
  );

  const redirectTo = (() => {
    if (status === 'investor') return '/account/investment';
    if (status === 'onlyBnpl' && bnplRedirectPending.current) return '/account/bnpl';
    if (status === 'noLoans' && !eligible) return '/account/bnpl';
    return null;
  })();

  React.useEffect(() => {
    if (status === 'loading' || redirectTo !== null) return;
    if (status === 'hasLoans') {
      analytics.page('PU - TU CREDITO', 'RESUMEN');
    } else if (eligible) {
      analytics.page('PU - TU CREDITO', 'OFERTA AVANCE');
    }
  }, [status, redirectTo, eligible]);

  if (status === 'loading') {
    return (
      <DataLoading
        paddingTop="15%"
        firstText="Estamos consultando tus datos..."
        secondText="En tu portal puedes gestionar tus pagos y revisar tu Poder de Compra para pagos en cuotas sin tarjeta"
      />
    );
  }

  if (redirectTo === '/account/bnpl' && status === 'onlyBnpl') {
    return <RedirectOnce to={redirectTo} sessionKey={BNPL_REDIRECT_KEY} />;
  }
  if (redirectTo) return <Redirect to={redirectTo} />;

  const showOfferDialog = eligible && status !== 'onlyBnpl';

  return (
    <Grid container sx={{ paddingBottom: { xs: 1, md: 2.5 } }}>
      {showOfferDialog && (
        <AdvanceOfferDialog token={token} origin="portal-usuario" currentPlrStep={plrStep} />
      )}
      {status === 'hasLoans' && (
        <>
          <Grid item xs={12} marginTop={2} />
          <ViewTitle
            title={`Hola ${firstName}`}
            color="secondary"
            variant="h4"
          />
          <ViewTitle
            lg={11}
            subtitle="Aquí podras ver el detalle de los créditos que se gestionan en nuestra plataforma"
            color="alternativePrimary"
            variant="h6"
          />
          {nonBnplLoans.map((loan: LoanOverViewType, index: number) => (
            <Box key={loan.loan.id} sx={{ width: '100%' }}>
              <LoanOverview
                token={token}
                loanData={loan}
                defaultExpanded={nonBnplLoans.length === 1}
                index={index}
              />
            </Box>
          ))}
          {eligible && (
            <Grid item xs={0} md={5} sx={{ paddingInline: '1%', marginTop: 1, display: { xs: 'none', md: 'block' } }}>
              <AdvanceRequestCard token={token} origin="portal-usuario" variant="inline" currentPlrStep={plrStep} />
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={3} sx={{ paddingInline: '1%', marginTop: { xs: 2, sm: 1 } }}>
            <Card sx={{
              ...sx().cardContainer,
              textAlign: 'left',
              flexDirection: 'column',
            }}
            >
              <Typography variant="body2" gutterBottom component="div">
                <b>[DPP]:</b>
                {' '}
                Descuento Por Planilla
              </Typography>
              <Typography variant="body2" component="div">
                <b>[PAC]:</b>
                {' '}
                Pago Automático de Cuentas

              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4} sx={{ paddingInline: '1%', marginTop: { xs: 5, sm: 1 } }}>
            <Card sx={{ ...sx().cardContainer, alignItems: 'center' }}>
              <SurveyCard />
            </Card>
          </Grid>
          {eligible && (
            <Grid item xs={12} md={0} sx={{ paddingInline: '1%', marginTop: 5, display: { xs: 'block', md: 'none' } }}>
              <AdvanceRequestCard token={token} origin="portal-usuario" variant="inline" currentPlrStep={plrStep} />
            </Grid>
          )}
        </>
      )}
      {status !== 'hasLoans' && (
        <>
          <Grid item xs={12} sx={{ paddingInline: '1%', mt: 3, mb: 3 }}>
            <NoActiveLoansBanner firstName={firstName} />
          </Grid>
          {eligible && (
            <Grid item xs={12} md={6} sx={{ paddingInline: '1%', marginTop: { xs: 2, sm: 1 } }}>
              <AdvanceRequestCard token={token} origin="portal-usuario" currentPlrStep={plrStep} />
            </Grid>
          )}
          <Grid item xs={12} md={6} sx={{ paddingInline: '1%', marginTop: { xs: 2, sm: 1 } }}>
            <ShoppingRedirectCard />
          </Grid>
        </>
      )}
    </Grid>
  );
}
